import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Tooltip } from '@mui/material';
import {
  faFileArrowDown as ExportListIcon,
  faBarcode as BardCodeIcon,
  faTrash as DeleteIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';

import { searchItemsToExportV2, deleteItems } from '~services/item';
import {
  useAppSelector,
  useLazyRequest,
  useExportTickets,
  useExportTicketsMl,
  useToggle,
} from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { isOnlyReadUser } from '~utils/user';
import { formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import DialogConfimation from '~components/DialogConfimation';

import { useDashboardVariantV2GiverContext } from '../../../../../../Context';
import { useDashboardVariantV2GiverContentInPreparationTabContext } from '../../../Context';

import {
  DashboardVariantV2GiverContentActionsIconButton,
  DashboardVariantV2GiverContentActionsIconButtonIcon,
  DashboardVariantV2GiverContentActionsIconButtonImg,
} from '../../../../../styles';

const DashboardVariantV2GiverContentInPreparationTabActionsActionables =
  (): ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAppSelector(selectAuth);

    const {
      actions: {
        ignoreRefreshBySocket: { toggleValue: toggleIgnoreRefreshBySocket },
      },
    } = useDashboardVariantV2GiverContext();

    const {
      selection: { selectedIds, utils: selectedIdsUtils },
      parseFiltersRequest,
      table: {
        queryParams: { pagination },
      },
      actions: { reloadDataAndResetSelectedRows },
    } = useDashboardVariantV2GiverContentInPreparationTabContext();

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
    const isEmptySelectedIds = useMemo(
      () => selectedIdsUtils.isEmpty(),
      [selectedIdsUtils],
    );
    const handleIgnoreRefreshBySocket = useCallback(() => {
      toggleIgnoreRefreshBySocket(true);
    }, [toggleIgnoreRefreshBySocket]);

    const [, loadingSearchToExport, , executeSearchToExport] = useLazyRequest({
      request: searchItemsToExportV2,
      withPostSuccess: (response) => {
        if (response.headers && response.data) {
          const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

          const defaultFileName = `pedidos-en-preparacion-${currentDate}.csv`;

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(`Se ha exportado los pedidos correctamente`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            'No se pudo exportar los pedidos, intente nuevamente',
            { variant: 'error' },
          );
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo exportar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      },
    });
    const handleExportItems = useCallback(async () => {
      await executeSearchToExport({
        filters: parseFiltersRequest(),
        pagination,
      });
    }, [executeSearchToExport, pagination, parseFiltersRequest]);

    const { loading: loadingExportTickets, onExportTickets } =
      useExportTickets();
    const handleExportTickets = useCallback(async () => {
      if (!isEmptySelectedIds) {
        await onExportTickets({
          itemIds: selectedIds,
        });
      }
    }, [isEmptySelectedIds, selectedIds, onExportTickets]);

    const { loading: loadingExportTicketsMl, onExportTicketsMl } =
      useExportTicketsMl();
    const handleExportTicketsMl = useCallback(async () => {
      if (!isEmptySelectedIds) {
        await onExportTicketsMl(selectedIds);
      }
    }, [isEmptySelectedIds, onExportTicketsMl, selectedIds]);

    const [openConfirmDeleteOrders, toggleOpenConfirmDeleteOrders] =
      useToggle(false);
    const [, loadingDeleteOrders, , executeDeleteOrders] = useLazyRequest({
      request: deleteItems,
      withPostSuccess: () => {
        enqueueSnackbar('Pedidos eliminados correctamente', {
          variant: 'success',
        });

        handleIgnoreRefreshBySocket();

        toggleOpenConfirmDeleteOrders(false);

        reloadDataAndResetSelectedRows();
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo elimnar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      },
    });
    const handleDeleteOrders = useCallback(async () => {
      if (!isEmptySelectedIds) {
        await executeDeleteOrders(selectedIds);
      }
    }, [executeDeleteOrders, isEmptySelectedIds, selectedIds]);

    return (
      <Stack direction="row" spacing={1.5}>
        <Tooltip title="Exportar listado" arrow>
          <DashboardVariantV2GiverContentActionsIconButton
            color="secondary"
            variant="outlined"
            onClick={handleExportItems}
            loading={loadingSearchToExport}
          >
            <DashboardVariantV2GiverContentActionsIconButtonIcon
              icon={ExportListIcon}
            />
          </DashboardVariantV2GiverContentActionsIconButton>
        </Tooltip>

        <Tooltip title="Exportar etiquetas" arrow>
          <span>
            <DashboardVariantV2GiverContentActionsIconButton
              color="primary"
              variant="outlined"
              onClick={handleExportTickets}
              loading={loadingExportTickets}
              disabled={isEmptySelectedIds}
              withEnabledPulse
            >
              <DashboardVariantV2GiverContentActionsIconButtonIcon
                icon={BardCodeIcon}
              />
            </DashboardVariantV2GiverContentActionsIconButton>
          </span>
        </Tooltip>

        {!isUserOnlyReader && (
          <Tooltip title="Exportar etiquetas de Mercado Libre" arrow>
            <span>
              <DashboardVariantV2GiverContentActionsIconButton
                color="warning"
                variant="outlined"
                onClick={handleExportTicketsMl}
                loading={loadingExportTicketsMl}
                disabled={isEmptySelectedIds}
                withEnabledPulse
              >
                <DashboardVariantV2GiverContentActionsIconButtonImg
                  src={toAbsoluteUrl('/images/icon_source_mp@2x.png')}
                  alt="MercadoLibre Logo"
                />
              </DashboardVariantV2GiverContentActionsIconButton>
            </span>
          </Tooltip>
        )}

        {!isUserOnlyReader && (
          <Tooltip title="Eliminar pedidos" arrow>
            <span>
              <DashboardVariantV2GiverContentActionsIconButton
                color="error"
                variant="outlined"
                onClick={() => toggleOpenConfirmDeleteOrders(true)}
                disabled={isEmptySelectedIds}
                withEnabledPulse
              >
                <DashboardVariantV2GiverContentActionsIconButtonIcon
                  icon={DeleteIcon}
                />
              </DashboardVariantV2GiverContentActionsIconButton>
            </span>
          </Tooltip>
        )}

        <DialogConfimation
          open={openConfirmDeleteOrders}
          description="Los pedidos seleccionados serán eliminados"
          onConfirm={handleDeleteOrders}
          onCancel={() => toggleOpenConfirmDeleteOrders(false)}
          loadingConfirm={loadingDeleteOrders}
        />
      </Stack>
    );
  };

export default DashboardVariantV2GiverContentInPreparationTabActionsActionables;
